import dayjs from 'dayjs';
import { getDaysAbsent, getDaysUntil } from './date.ts';
import {
  getAppMeta,
  setAppMeta,
  getCrmClients,
  saveCrmClients,
  saveCustomerStatsBatch,
} from '../lib/db/index.ts';
import { getAppointmentLog } from '../lib/api.ts';
import { useCrmStore } from '../stores/crm.store.ts';

export interface AppointmentLogRow {
  customerId: string;
  customerName: string;
  appointmentDate: string;
  appointmentTime: string;
  serviceName: string;
  staffName: string;
  locationName: string;
  locationId: string;
  status: string;
  createdAt: string;
  price: number;
}

export interface DeltaPatch {
  customerId: string;
  nextAppointmentDate?: string;
  nextStaffMember?: string;
  nextServiceName?: string;
  nextLocationName?: string;
  nextLocationId?: string;
  daysUntilNext?: number;
  lastAppointmentDate?: string;
  lastStaffMember?: string;
  lastServiceName?: string;
  lastLocationName?: string;
  lastLocationId?: string;
  daysAbsent?: number;
}

export interface DeltaSyncResult {
  patchedCount: number;
  newHighWaterMark: string;
  skipped: boolean;
  pagesFetched: number;
}

/**
 * Pure function: given a list of appointment log rows (sorted by created_at DESC)
 * and a map of existing clients, produces a list of surgical patches.
 */
export function extractDeltaPatches(
  logRows: AppointmentLogRow[],
  existingClientsMap: Map<string, { lastAppointmentDate?: string; nextAppointmentDate?: string }>
): DeltaPatch[] {
  const now = dayjs().startOf('day');
  const patchMap = new Map<string, DeltaPatch>();

  for (const row of logRows) {
    const cid = row.customerId;
    if (!cid || !existingClientsMap.has(cid)) continue;

    const apptDate = dayjs(row.appointmentDate).startOf('day');
    const isFuture = apptDate.isSame(now) || apptDate.isAfter(now);
    const isCancelled = row.status === 'cancelled' || row.status === 'no-show';

    if (isCancelled) continue;

    if (!patchMap.has(cid)) {
      patchMap.set(cid, { customerId: cid });
    }
    const patch = patchMap.get(cid)!;

    if (isFuture) {
      // Pick closest upcoming appointment
      if (!patch.nextAppointmentDate || apptDate.isBefore(dayjs(patch.nextAppointmentDate))) {
        patch.nextAppointmentDate = row.appointmentDate;
        patch.nextStaffMember = row.staffName || undefined;
        patch.nextServiceName = row.serviceName || undefined;
        patch.nextLocationName = row.locationName || undefined;
        patch.nextLocationId = row.locationId || undefined;
        patch.daysUntilNext = getDaysUntil(row.appointmentDate);
      }
    } else {
      // Pick most recent past visit (completed / confirmed)
      if (
        (row.status === 'completed' || row.status === 'arrived' || row.status === 'started' || row.status === 'confirmed') &&
        (!patch.lastAppointmentDate || apptDate.isAfter(dayjs(patch.lastAppointmentDate)))
      ) {
        patch.lastAppointmentDate = row.appointmentDate;
        patch.lastStaffMember = row.staffName || undefined;
        patch.lastServiceName = row.serviceName || undefined;
        patch.lastLocationName = row.locationName || undefined;
        patch.lastLocationId = row.locationId || undefined;
        patch.daysAbsent = getDaysAbsent(row.appointmentDate);
      }
    }
  }

  // Filter out patches that have no meaningful changes
  return Array.from(patchMap.values()).filter(
    (p) => p.nextAppointmentDate || p.lastAppointmentDate
  );
}

/**
 * Parses the raw Fresha report-tables response into typed AppointmentLogRow[].
 */
export function parseReportRows(reportData: any): AppointmentLogRow[] {
  const included = reportData?.included || reportData?.data?.included || [];
  const table =
    included.find((inc: any) => inc.type === 'report-tables') ||
    included.find(
      (inc: any) =>
        inc.type === 'report-tables' &&
        (inc.id?.includes('appointment') || inc.id === 'list_title' || inc.id === 'appointment_list_table')
    );

  const rows = table?.attributes?.rows || [];
  return rows.map((row: any) => {
    const cells = row.cells || {};
    const scheduledOn = cells.scheduled_on?.value || '';
    const dateFromScheduled = scheduledOn ? scheduledOn.split('T')[0] : '';

    return {
      customerId: String(
        cells.customer_name?.record_id ||
        cells.customer_id?.value ||
        cells.client_id?.value ||
        cells.id?.value ||
        ''
      ),
      customerName: cells.customer_name?.value || cells.client_name?.value || cells.name?.value || '',
      appointmentDate: cells.scheduled_date?.value || dateFromScheduled || cells.date?.value || cells.appointment_date?.value || '',
      appointmentTime: cells.time?.value || cells.start_time?.value || '',
      serviceName: cells.service_name?.value || cells.service?.value || '',
      staffName: cells.employee_name?.value || cells.staff_name?.value || cells.staff?.value || cells.employee?.value || '',
      locationName: cells.location_name?.value || cells.location?.value || '',
      locationId: String(cells.location_id?.value || ''),
      status: cells.status_label_id?.value || cells.status?.value || 'booked',
      createdAt: cells.created_on?.value || cells.created_date?.value || cells.created_at?.value || row.created_at || '',
      price: Number(cells.price?.value || cells.total?.value || 0),
    };
  });
}

export const DELTA_SYNC_WATERMARK_KEY = 'delta_sync_last_created_at';
export const DELTA_SYNC_COOLDOWN_MS = 60_000; // 1 minute cooldown
export const DELTA_SYNC_COOLDOWN_KEY = 'delta_sync_last_run_at';

export interface DeltaSyncDependencies {
  getAppMeta: typeof import('../lib/db/index.ts').getAppMeta;
  setAppMeta: typeof import('../lib/db/index.ts').setAppMeta;
  getCrmClients: typeof import('../lib/db/index.ts').getCrmClients;
  saveCrmClients: typeof import('../lib/db/index.ts').saveCrmClients;
  saveCustomerStatsBatch: typeof import('../lib/db/index.ts').saveCustomerStatsBatch;
  getAppointmentLog: typeof import('../lib/api.ts').getAppointmentLog;
  getStore: () => typeof import('../stores/crm.store.ts').useCrmStore;
}

/**
 * Orchestrates the full delta sync cycle with Convergence Pagination & Safety Cap:
 * 1. Read high-water mark from Dexie (appMeta: delta_sync_last_created_at)
 * 2. Paginate appointment log until convergence (row.createdAt <= highWaterMark) or MAX_PAGES cap
 * 3. Match new rows to existing crmClients
 * 4. Patch Dexie and Zustand store
 * 5. Advance high-water mark
 */
export async function runDeltaSync(
  workspaceId: string,
  locationId?: string,
  _deps?: Partial<DeltaSyncDependencies>
): Promise<DeltaSyncResult> {
  const deps = await resolveDependencies(_deps);

  // Cooldown check
  const lastRunAt = await deps.getAppMeta<number>(DELTA_SYNC_COOLDOWN_KEY);
  if (lastRunAt && Date.now() - lastRunAt < DELTA_SYNC_COOLDOWN_MS) {
    return { patchedCount: 0, newHighWaterMark: '', skipped: true, pagesFetched: 0 };
  }

  // 1. Read high-water mark
  const lastCreatedAt = (await deps.getAppMeta<string>(DELTA_SYNC_WATERMARK_KEY)) || '';

  // 2. Convergence pagination loop with safety cap (max 10 pages = 250 rows)
  const allNewRows: AppointmentLogRow[] = [];
  const limit = 25;
  let offset = 0;
  let converged = false;
  let pagesFetched = 0;
  const MAX_PAGES = 10;
  let latestSeenCreatedAt = '';

  while (!converged && pagesFetched < MAX_PAGES) {
    try {
      const reportData = await deps.getAppointmentLog({
        workspaceId,
        limit,
        offset,
        locationId: locationId || undefined,
      });
      pagesFetched++;

      const pageRows = parseReportRows(reportData);
      if (pageRows.length === 0) {
        converged = true;
        break;
      }

      if (!latestSeenCreatedAt && pageRows[0]?.createdAt) {
        latestSeenCreatedAt = pageRows[0].createdAt;
      }

      for (const row of pageRows) {
        if (lastCreatedAt && row.createdAt <= lastCreatedAt) {
          converged = true;
          break;
        }
        allNewRows.push(row);
      }

      if (!converged && pageRows.length === limit) {
        offset += limit;
      } else {
        converged = true;
      }
    } catch (err) {
      console.warn('Delta sync: error fetching appointment log page at offset', offset, err);
      break;
    }
  }

  if (allNewRows.length === 0) {
    await deps.setAppMeta(DELTA_SYNC_COOLDOWN_KEY, Date.now());
    if (latestSeenCreatedAt && !lastCreatedAt) {
      await deps.setAppMeta(DELTA_SYNC_WATERMARK_KEY, latestSeenCreatedAt);
    }
    return { patchedCount: 0, newHighWaterMark: latestSeenCreatedAt || lastCreatedAt, skipped: false, pagesFetched };
  }

  // 3. Build existing clients map from Dexie
  const existingClients = await deps.getCrmClients(workspaceId);
  const clientsMap = new Map<string, { lastAppointmentDate?: string; nextAppointmentDate?: string }>();
  for (const c of existingClients) {
    clientsMap.set(c.id, { lastAppointmentDate: c.lastAppointmentDate, nextAppointmentDate: c.nextAppointmentDate });
    if (c.customerId && c.customerId !== c.id) {
      clientsMap.set(c.customerId, { lastAppointmentDate: c.lastAppointmentDate, nextAppointmentDate: c.nextAppointmentDate });
    }
  }

  // 4. Extract patches
  const patches = extractDeltaPatches(allNewRows, clientsMap);

  const newMark = latestSeenCreatedAt || allNewRows[0]?.createdAt || lastCreatedAt;

  if (patches.length === 0) {
    if (newMark) await deps.setAppMeta(DELTA_SYNC_WATERMARK_KEY, newMark);
    await deps.setAppMeta(DELTA_SYNC_COOLDOWN_KEY, Date.now());
    return { patchedCount: 0, newHighWaterMark: newMark, skipped: false, pagesFetched };
  }

  // 5. Apply patches to Dexie (crmClients)
  const patchedClients = existingClients
    .filter((c) => patches.some((p) => p.customerId === c.id || p.customerId === c.customerId))
    .map((c) => {
      const patch = patches.find((p) => p.customerId === c.id || p.customerId === c.customerId);
      if (!patch) return c;
      return {
        ...c,
        ...(patch.nextAppointmentDate && { nextAppointmentDate: patch.nextAppointmentDate }),
        ...(patch.nextStaffMember && { nextStaffMember: patch.nextStaffMember }),
        ...(patch.nextServiceName && { nextServiceName: patch.nextServiceName }),
        ...(patch.nextLocationName && { nextLocationName: patch.nextLocationName }),
        ...(patch.nextLocationId && { nextLocationId: patch.nextLocationId }),
        ...(patch.daysUntilNext !== undefined && { daysUntilNext: patch.daysUntilNext }),
        ...(patch.lastAppointmentDate && { lastAppointmentDate: patch.lastAppointmentDate }),
        ...(patch.lastStaffMember && { lastStaffMember: patch.lastStaffMember }),
        ...(patch.lastServiceName && { lastServiceName: patch.lastServiceName }),
        ...(patch.lastLocationName && { lastLocationName: patch.lastLocationName }),
        ...(patch.lastLocationId && { lastLocationId: patch.lastLocationId }),
        ...(patch.daysAbsent !== undefined && { daysAbsent: patch.daysAbsent }),
      };
    });

  await deps.saveCrmClients(patchedClients);

  // 6. Apply patches to Zustand store (reactive UI update)
  try {
    const store = deps.getStore();
    const updatesMap: Record<string, Partial<any>> = {};
    for (const patch of patches) {
      const updateFields: Record<string, any> = {};
      if (patch.nextAppointmentDate) updateFields.nextAppointmentDate = patch.nextAppointmentDate;
      if (patch.nextStaffMember) updateFields.nextStaffMember = patch.nextStaffMember;
      if (patch.nextServiceName) updateFields.nextServiceName = patch.nextServiceName;
      if (patch.nextLocationName) updateFields.nextLocationName = patch.nextLocationName;
      if (patch.nextLocationId) updateFields.nextLocationId = patch.nextLocationId;
      if (patch.daysUntilNext !== undefined) updateFields.daysUntilNext = patch.daysUntilNext;
      if (patch.lastAppointmentDate) updateFields.lastAppointmentDate = patch.lastAppointmentDate;
      if (patch.lastStaffMember) updateFields.lastStaffMember = patch.lastStaffMember;
      if (patch.lastServiceName) updateFields.lastServiceName = patch.lastServiceName;
      if (patch.lastLocationName) updateFields.lastLocationName = patch.lastLocationName;
      if (patch.daysAbsent !== undefined) updateFields.daysAbsent = patch.daysAbsent;
      if (Object.keys(updateFields).length > 0) {
        updatesMap[patch.customerId] = updateFields;
      }
    }
    if (Object.keys(updatesMap).length > 0) {
      store.getState().updateClientsBatch(updatesMap);
    }
  } catch {
    // Store might not be mounted in test runner
  }

  // 7. Update high-water mark in Dexie
  if (newMark) {
    await deps.setAppMeta(DELTA_SYNC_WATERMARK_KEY, newMark);
  }
  await deps.setAppMeta(DELTA_SYNC_COOLDOWN_KEY, Date.now());

  return { patchedCount: patches.length, newHighWaterMark: newMark, skipped: false, pagesFetched };
}

function resolveDependencies(overrides?: Partial<DeltaSyncDependencies>): DeltaSyncDependencies {
  return {
    getAppMeta: overrides?.getAppMeta || getAppMeta,
    setAppMeta: overrides?.setAppMeta || setAppMeta,
    getCrmClients: overrides?.getCrmClients || getCrmClients,
    saveCrmClients: overrides?.saveCrmClients || saveCrmClients,
    saveCustomerStatsBatch: overrides?.saveCustomerStatsBatch || saveCustomerStatsBatch,
    getAppointmentLog: overrides?.getAppointmentLog || getAppointmentLog,
    getStore: overrides?.getStore || (() => useCrmStore),
  };
}
